import {
  createElement,
  forwardRef,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  useEffect,
  useCallback,
} from 'react';
import { PickerProps } from '../__internal__';
import { Select } from '../../Select';
import { Flow } from '../../Layouts/Flow';
import { withLabels } from '../../withLabels';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';
import { isThaiLocale } from '../../../locale/localeUtils';
import { IntegerInputRef } from '../__internal__/';
import { PickerRef } from './PickerRef';
import { YearInput } from './YearInput';
import {
  getYearOrder,
  convertGregorianYearToThaiYear,
  convertThaiYearToGregorianYear,
} from '../DateUtil';
import { useDefaultDateTimeFormat } from '../useDefaultDateTimeFormat';
import { postfixTestId } from '../../../util/testId';

import '../DatePicker.css';

const SelectWithLabels = withLabels(Select);

export const QuarterPicker = forwardRef<PickerRef, PickerProps>(function QuarterPicker(props, ref) {
  const localize = useNCTextLocalizer('DatePicker');
  const locale = useFormatLocale();

  const yearOrder = useMemo(() => getYearOrder(locale), [locale]);

  const minMonthAllowed = props.minDate?.getMonth() || 0;
  const maxMonthAllowed = props.maxDate?.getMonth() || 11;
  const minYearAllowed = props.minDate?.getFullYear() || 1582;
  const maxYearAllowed = props.maxDate?.getFullYear() || 9999;
  const [isQuarterValid, setIsQuarterValid] = useState(true);

  const defaultFormat = useDefaultDateTimeFormat({
    type: 'quarter',
  });

  const minMaxQuarterErrorMsg = localize('minmax.quarter.error.msg', {
    min: defaultFormat(props.minDate ?? new Date(1582, 0, 1)),
    max: defaultFormat(props.maxDate ?? new Date(9999, 11, 31)),
  });

  const getQuarterValue = (value: number) => {
    if (value >= 0 && value < 3) return 1;
    if (value >= 3 && value < 6) return 2;
    if (value >= 6 && value < 9) return 3;
    if (value >= 9 && value < 12) return 4;
    return 1;
  };

  const validateQuarterValue = useCallback(
    (value: number) => {
      const minQtr = getQuarterValue(minMonthAllowed),
        maxQtr = getQuarterValue(maxMonthAllowed);
      if (
        (props.value.getFullYear().toString().length >= 4 &&
          value < minQtr &&
          props.value.getFullYear() <= minYearAllowed) ||
        (value > maxQtr && props.value.getFullYear() >= maxYearAllowed)
      )
        setIsQuarterValid(false);
      else setIsQuarterValid(true);
    },
    [maxMonthAllowed, maxYearAllowed, minMonthAllowed, minYearAllowed, props.value]
  );

  const refSelect = useRef<HTMLDivElement>(null);
  const refInput = useRef<IntegerInputRef>(null);
  useImperativeHandle(ref, () => ({
    isValid: () => {
      if (!refInput.current?.isValid() || !isQuarterValid) return false;
      return true;
    },
    focus: () => (!isQuarterValid ? refSelect.current?.focus() : refInput.current?.focus()),
  }));

  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      refSelect.current?.focus();
    });
    refSelect.current?.setAttribute('data-autofocus', 'true');
  }, []);

  useEffect(() => {
    const qtr = getQuarterValue(props.value.getMonth());
    // Need to briefly delay validation when opening the dialog so the error message is displayed in
    // the proper location if invalid.  This small delay ensures the dialog is opened and controls are visible.
    setTimeout(() => {
      validateQuarterValue(qtr);
      setIsValid(refInput.current?.isValid() ?? true);
    }, 0);
  }, [props.value, validateQuarterValue]);

  const yearElement = (
    <YearInput
      testId={postfixTestId(props.testId, 'yearInput')}
      ref={refInput}
      value={
        isThaiLocale(locale)
          ? convertGregorianYearToThaiYear(props.value.getFullYear())
          : props.value.getFullYear()
      }
      onChange={(nextYear) => {
        const nextValue = new Date(props.value);
        nextValue.setFullYear(
          isThaiLocale(locale) ? convertThaiYearToGregorianYear(nextYear) : nextYear
        );
        const isValidDate = nextValue instanceof Date && !isNaN(nextValue.getTime());
        if (!isValidDate) {
          setIsValid(false);
          return;
        }
        props.onChange(nextValue);
      }}
      minDate={props.minDate}
      maxDate={props.maxDate}
    />
  );

  return (
    <Flow spacing="s" orientation="horizontal">
      {yearOrder === 'before' && yearElement}
      <SelectWithLabels
        testId={postfixTestId(props.testId, 'quarterSelect')}
        selectBoxRef={refSelect}
        selectedIndex={Math.floor(props.value.getMonth() / 3)}
        values={[1, 2, 3, 4].map((i) => ({ text: localize(`quarter.${i}.menuItem.label`) }))}
        onSelected={(selectedIndex) => {
          const nextValue = new Date(props.value);
          nextValue.setMonth((selectedIndex ?? 0) * 3);
          props.onChange(nextValue);
          validateQuarterValue((selectedIndex ?? 0) + 1);
        }}
        message={!isQuarterValid ? minMaxQuarterErrorMsg : undefined}
        status={isValid && !isQuarterValid ? 'alert' : undefined}
        disabled={!isValid}
        label={localize('quarter.label')}
        labelPosition="top"
        wrapLabel={false}
      />
      {yearOrder === 'after' && yearElement}
    </Flow>
  );
});
